/**
 * Event emitter for product analytics. No personal data, no biometrics — only
 * aggregate interaction and performance signals.
 *
 * Phase 1 sinks: GA4 (`gtag` / `dataLayer`) and Shopify web pixels
 * (`Shopify.analytics.publish`) when present, console in dev. Phase 4 adds the
 * pixel extension and the event dictionary.
 */
export type TryOnEventName =
  | 'tryon_open'
  | 'tryon_camera_granted'
  | 'tryon_camera_denied'
  | 'tryon_engine_ready'
  | 'tryon_tracking_acquired'
  | 'tryon_tracking_lost'
  | 'tryon_quality_change'
  | 'tryon_error'
  | 'tryon_close'
  | 'tryon_lensview_open'
  | 'tryon_lensview_close'
  | 'tryon_order_open'
  | 'tryon_order_whatsapp'
  /** A110W: step 2 of the WhatsApp order — the photo was handed over (`method`: share | download). */
  | 'tryon_order_whatsapp_photo'
  | 'tryon_order_share'
  | 'tryon_order_pdf'
  | 'tryon_order_cart'
  | 'tryon_order_cart_error'
  /** A110X: the try-on came back from a sleep (`reason`: hidden | stall, `duration_ms`) or restarted a part (`part`: camera | tracker | all). */
  | 'tryon_recover'
  /** A110X: the « Relancer » message was shown (`problem`: video | tracker | render | slow). */
  | 'tryon_recover_message'
  /** A110Z: the engraving view was opened (phone full-screen view). Never the texts. */
  | 'tryon_engrave_open';

export interface TryOnEventParams {
  product_handle?: string;
  frame_id?: string;
  tracker_mode?: string;
  delegate?: string;
  init_ms?: number;
  avg_fps?: number;
  infer_ms?: number;
  quality_level?: string;
  reason?: string;
  duration_ms?: number;
  losses?: number;
  reacquire_ms?: number;
  code?: string;
  message?: string;
  /** A110T: Sunset lens shown in « Voir le paysage ». A110V: lens id of the pair ordered. */
  lens?: string;
  /** A202: the Sunset supplement of a Signature in the cart — `added` with the frame, or `missing` (frame alone). */
  supplement?: string;
  /** A110V: acetate id of the pair ordered (`original` or a library id). Never the PD or the photo. */
  acetate?: string;
  /**
   * A110V: how the try-on photo left the page — `share` (system share sheet) or `download`.
   * A110W: same values for `tryon_order_whatsapp_photo` (step 2 of the WhatsApp order).
   */
  method?: string;
  /** A110X: part restarted after a sleep (camera | tracker | all) or `resume` for the light cure. */
  part?: string;
  /**
   * A110X: what still went wrong when the « Relancer » message was shown.
   * A110Y: `restarts` | `context` | `memory` | `again` when the message asks to refresh the page.
   */
  problem?: string;
  /** A110Y, `tryon_close`: WebGL textures / geometries / programs alive at the close, and the drawing buffer in pixels. */
  gl_textures?: number;
  gl_geometries?: number;
  gl_programs?: number;
  gl_pixels?: number;
  /** A110Y, `tryon_close`: automatic restarts and 3D context losses in the session. */
  restarts?: number;
  context_losses?: number;
}

interface AnalyticsWindow extends Window {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  Shopify?: { analytics?: { publish?: (name: string, payload: unknown) => void } };
}

let debugSink = false;

export function enableAnalyticsDebug(enabled: boolean): void {
  debugSink = enabled;
}

export function track(name: TryOnEventName, params: TryOnEventParams = {}): void {
  const w = window as AnalyticsWindow;
  const payload = { ...params, ts: Date.now() };
  try {
    if (typeof w.gtag === 'function') w.gtag('event', name, payload);
    else if (Array.isArray(w.dataLayer)) w.dataLayer.push({ event: name, ...payload });
    w.Shopify?.analytics?.publish?.(name, payload);
  } catch {
    // Analytics must never break the experience.
  }
  if (debugSink) console.debug('[tryon:event]', name, payload);
}
